// src/dataProvider/resources/users.ts
import { httpClient } from "../../httpClient";
import { buildListQuery, withGlobalClubFilter, pickClubId } from "../clubScope";

const apiUrl = "/api";

const ROLE_LABELS: Record<string, string> = {
  member: "Mitglied",
  shop_admin: "Shop-Admin",
  team_admin: "Team-Admin",
  club_admin: "Club-Admin",
};

export { ROLE_LABELS };

export function usersResource(clubId: number | null) {
  return {
    getList: async (params: any) => {
      const paramsWithClub = withGlobalClubFilter(params, clubId);
      const qs = buildListQuery(paramsWithClub);

      const { json } = await httpClient(`${apiUrl}/users?${qs.toString()}`);
      const data = Array.isArray(json) ? json : json?.data ?? [];
      return { data, total: json?.total ?? data.length };
    },

    createInvite: async (params: any) => {
      const paramsWithClub =
        clubId && params?.data && params.data.clubId == null
          ? { ...params, data: { ...params.data, clubId } }
          : params;

      const { json } = await httpClient(`${apiUrl}/users/invite`, {
        method: "POST",
        body: JSON.stringify(paramsWithClub.data),
      });
      return { data: json };
    },

    resendInvite: async (params: any) => {
      const effectiveClubId = pickClubId(params, clubId);
      if (!effectiveClubId) throw new Error("clubId fehlt (bitte Verein auswählen)");

      const { userId } = params.data;
      const { json } = await httpClient(`${apiUrl}/users/resendInvite`, {
        method: "POST",
        body: JSON.stringify({ userId, clubId: effectiveClubId }),
      });

      const record = json?.data ?? json;
      if (!record?.id) record.id = userId;
      return { data: record };
    },

    teamAssignments: async (params: any) => {
      const { json } = await httpClient(`${apiUrl}/users/teamAssignments`, {
        method: "POST",
        body: JSON.stringify(params.data),
      });
      return { data: json?.data ?? json };
    },

    getOne: async (params: any) => {
      const qs = clubId ? `?club_id=${clubId}` : "";
      const { json } = await httpClient(`${apiUrl}/users/${params.id}${qs}`);
      return { data: json };
    },

    remove: async (params: any) => {
      const effectiveClubId = pickClubId(params, clubId);
      if (!effectiveClubId) throw new Error("clubId fehlt (bitte Verein auswählen)");

      await httpClient(`${apiUrl}/users/${params.id}?club_id=${effectiveClubId}`, {
        method: "DELETE",
      });
      return { data: params.previousData ?? { id: params.id } };
    },

    assignableRoles: async (params: any) => {
      const club = params?.filter?.clubId ?? clubId;
      const qs = club ? `?clubId=${encodeURIComponent(club)}` : "";

      const { json } = await httpClient(`${apiUrl}/users/assignable-roles${qs}`, {
        method: "GET",
      });

      const roles: string[] = Array.isArray(json?.roles) ? json.roles : [];
      const data = roles.map((r) => ({
        id: r,
        name: ROLE_LABELS[r] ?? r,
      }));

      return { data, total: data.length };
    },
  };
}
